// Реестр людей. Одна строка — одна грамматика (правило 2): фамилия и имя, объект, статус,
// метка долга. Над списком сказано, сколько человек показано и в каком состоянии («133 человека
// · на вахте») и на какой момент срез (generated_at, не часы браузера).
//
// ## Состояния и вкладки
//
// Реестр открывается на вкладке «На вахте»: финдиректору нужны те, кто на вахте, а уволенные и
// резерв «торчат все сразу» (29.09.2026). Остальных достаёт одно нажатие по вкладке, и числа
// на вкладках видны сразу. Какая вкладка открыта, решает ТОЛЬКО адрес (`registryTab`).
//
// Данные приходят ОДНИМ ответом без отбора по состоянию, а вкладки делят его на клиенте: число
// на вкладке и строки под ней из одного источника, переключение без сети. Серверный параметр
// `status` остаётся для других вызовов (Дела, мастер укомплектования), а сюда не едет.
//
// Остальной отбор (поиск, объект, регион, разряд, долг, тип оформления) лежит в адресе
// (правило 4) и целиком уезжает на сервер: фильтр, который украшает экран, но не меняет
// выборку, — это враньё в счётчике над списком.
//
// Правило 11: при смене отбора строки НЕ исчезают — keepPreviousData держит прежний список,
// скелет рисуется только когда данных нет даже в кэше прошлой сессии.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { RiAddLine, RiFilterOffLine, RiTeamLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { ListPane } from '@/layout/ListPane';
import type { EmployeeOut, EmployeesQuery } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import type { SearchState } from '@/shared/routing/search';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import * as Switch from '@/shared/ui/switch';

import { useSitesQuery } from '@/shared/org/queries';

import { BulkStatusBar } from '../components/BulkStatusBar';
import { ВкладкиСостояний, ВкладкиСостоянийСкелет } from '../components/ВкладкиСостояний';
import { запомнитьВыбор, запомнитьПриглашаемых } from '../panels/staff-selection';
import { RowCheckbox } from '@/shared/ui/row-checkbox';
import { GUARD_CATEGORIES, STATUS_COLORS, STATUS_TITLES } from '../dictionaries';
import { useEmployeesQuery, useEmploymentViewsQuery } from '../queries';
import { countTabs, describeEmpty, inTab, listCaption } from '../registry-tabs';
import type { PeopleRoute, PeopleView } from '../route';
import {
  DEFAULT_VIEW,
  FILTER_CATEGORY,
  FILTER_DEBT,
  FILTER_REGION,
  FILTER_SITE,
  FILTER_STATUS,
  PANEL_NEW_EMPLOYEE,
  NEW_EMPLOYEE_BLANK,
  STATUS_ALL,
  hasFilterNarrowing,
  openInviteHref,
  openPanelHref,
  openStaffHref,
  peopleHref,
  registryTab,
  setFilter,
} from '../route';

import { exportEmployees } from '@/shared/api/endpoints/people';
import { ExportButton } from '@/shared/reports/ExportButton';

/** Заведение и правка людей: server/app/api/v1/employees.py. */
const EDIT_PERMISSION = 'core.employee.edit';
/** Выдача ссылок для входа. Есть у администратора, финотдела, руководства и оперрука. */
const INVITE_PERMISSION = 'core.user.invite';

export interface EmployeesListProps {
  route: PeopleRoute;
  viewSwitcher: ReactNode;
}

/**
 * Отбор из адреса, переведённый в параметры запроса. Единственное место, где адрес
 * превращается в запрос: два таких места разъезжаются, и человек видит в списке не то,
 * что написано в его же ссылке.
 *
 * Состояния здесь НЕТ намеренно (`status` не уходит на сервер никогда). Реестр едет одним
 * ответом по всем состояниям, а вкладки делят его на клиенте, и счётчики на вкладках берутся
 * из того же ответа. Умолчание «На вахте» нельзя класть и в общий хук `useEmployeesQuery` или
 * в `listEmployees`: ими пользуются Ctrl+K, выбор человека в мастерах и кэш `peopleKeys`, и они
 * обязаны искать по всем. Место умолчанию — `registryTab` и слой показа ниже.
 */
export function queryFromRoute(view: PeopleView, search: SearchState): EmployeesQuery {
  const category = search.filters[FILTER_CATEGORY];
  return {
    site_id: search.filters[FILTER_SITE],
    region: search.filters[FILTER_REGION],
    guard_category: category === undefined ? undefined : Number(category),
    has_debt: search.filters[FILTER_DEBT] === 'true' ? true : undefined,
    employment_type_id:
      view.kind === 'employment' && view.typeId !== null ? view.typeId : undefined,
    q: search.q,
  };
}

/**
 * Метка справа в строке списка. В списке «Все» это состояние; на вкладке одного состояния метки нет,
 * кроме уволенных: у них стоит, когда уволен, потому что по этому списку ищут «кого уволили недавно».
 */
function метка(employee: EmployeeOut, смешанныеСостояния: boolean) {
  const уволен = employee.status === 'fired';
  if (!смешанныеСостояния && !уволен) {
    return undefined;
  }
  const дата =
    уволен && employee.fired_at !== null && employee.fired_at !== undefined
      ? ` ${formatDateOnly(employee.fired_at)}`
      : '';
  if (!смешанныеСостояния && дата === '') {
    return undefined;
  }
  return (
    <Badge.Root variant='lighter' color={STATUS_COLORS[employee.status]} size='medium'>
      {STATUS_TITLES[employee.status]}
      {дата}
    </Badge.Root>
  );
}

export function EmployeesList({ route, viewSwitcher }: EmployeesListProps) {
  const navigate = useNavigate();
  const { view, search, rowId } = route;
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);
  const canInvite = can(permissions, INVITE_PERMISSION);
  // Причина отказа появляется по нажатию, а не висит на экране заранее: заранее это ещё одна
  // строка, которую человек читает каждый раз, а нажимает он кнопку один раз.
  const [запретЗаведения, setЗапретЗаведения] = useState<string | null>(null);

  const params = queryFromRoute(view, search);
  const query = useEmployeesQuery(params);
  // Тот же ключ кэша, что у переключателя представлений: второго запроса не будет.
  const views = useEmploymentViewsQuery();

  // Какая вкладка состояния открыта — решает адрес и больше ничего. Не данные: иначе вкладка
  // мигала бы, пока едет ответ сервера («Все», а через секунду «На вахте»).
  const вкладка = registryTab(view, search);

  // Выделение строк живёт в памяти экрана, а не в адресе: это не срез данных, а намерение
  // прямо сейчас. Ссылка «вот эти семнадцать человек» ничего бы коллеге не сказала.
  const [selected, setSelected] = useState<readonly string[]>([]);

  // Сменили отбор, представление ИЛИ ВКЛАДКУ — прежнее выделение бессмысленно: половины строк
  // на экране уже нет, а действие пачкой применилось бы и к ним. Вкладка входит в подпись
  // отдельно: состояние в запрос не едет, и без неё переключение вкладок выделение не трогало
  // бы, а «Сменить статус пачкой» ушла бы по людям, которых человек на экране уже не видит.
  const filterSignature = JSON.stringify({ params, вкладка });
  useEffect(() => {
    setSelected([]);
  }, [filterSignature]);

  function apply(next: SearchState) {
    // replace: в истории браузера один шаг на подбор отбора, а не двадцать.
    navigate(peopleHref(view, next, rowId), { replace: true });
  }

  const items = query.data?.items ?? [];
  // Представление «тип не проставлен» сервер отдельным отбором не поддерживает: параметр
  // employment_type_id умеет только «равно значению». Срез считается по уже приехавшей
  // странице, а счётчик над списком берётся из /employees/views — он посчитан сервером.
  const filterNoType = view.kind === 'employment' && view.typeId === null;
  const rows = filterNoType ? items.filter((item) => item.employment_type_id === null) : items;

  // Вкладки и числа на них честны, только пока приехали ВСЕ люди выборки. Страница неполная —
  // число на вкладке было бы числом на куске, выданным за итог: вкладок нет, строки идут как
  // пришли, а человеку говорит прежняя подсказка «Показаны первые N из M».
  const страницаПолная = query.data !== undefined && query.data.items.length >= query.data.total;
  const счёт = countTabs(rows);
  const видимые = страницаПолная ? inTab(rows, вкладка) : rows;
  // Вкладка так, как её видит человек: при неполной странице вкладок нет, и показаны все.
  const показаннаяВкладка = страницаПолная ? вкладка : STATUS_ALL;
  // Состояние для выгрузки: вкладка, если она что-то отбирает, и ничего, пока данных нет или
  // страница неполная (тогда строки идут без деления по вкладкам, и файл должен быть таким же).
  const статусВыгрузки =
    вкладка !== STATUS_ALL && (query.data === undefined || страницаПолная) ? вкладка : undefined;

  // Счётчик над списком: число людей на открытой вкладке, из того же ответа, что и строки.
  // Страница неполная — вкладок нет, и честное число только серверное: total выборки, а для
  // среза «тип не проставлен», где сервер отбирать не умеет, — из /employees/views.
  const noTypeCount = views.data?.items.find((item) => item.employment_type_id === null)?.count;
  const captionCount = страницаПолная
    ? видимые.length
    : filterNoType
      ? noTypeCount
      : query.data?.total;

  // Поиск живёт отдельно от отборов и всегда на виду: им ищут КОНКРЕТНОГО человека, и делают
  // это чаще, чем пользуются всеми отборами вместе.
  const поиск = (
    <SearchFilter
      label='Поиск человека по фамилии, имени, табельному номеру или телефону'
      placeholder='Фамилия, табельный номер, телефон'
      value={search.q ?? ''}
      onApply={(value) => apply({ ...search, q: value === '' ? undefined : value })}
    />
  );

  // Названия наложенных отборов идут в подпись свёрнутой полосы: человек видит девять фамилий
  // из двухсот восьмидесяти шести и обязан понимать, это весь реестр или срез — и какой.
  //
  // Состояния среди них нет: оно написано вкладкой над поиском и подписью над списком
  // («133 человека · на вахте»). Во второй раз «статус» здесь читался бы как ещё один отбор,
  // а на чистом адресе, где открыта вкладка «На вахте», стоял бы постоянно.
  const наложено = [
    search.filters[FILTER_CATEGORY] === undefined ? null : 'разряд',
    search.filters[FILTER_SITE] === undefined ? null : 'объект',
    search.filters[FILTER_REGION] === undefined ? null : 'регион',
    search.filters[FILTER_DEBT] === undefined ? null : 'только с долгом',
  ].filter((имя): имя is string => имя !== null);

  // Состояние в отборах больше не выбирается: им управляют вкладки над поиском, и второй
  // выбор того же самого спорил бы с ними. На чистом адресе открыта «На вахте», а поле
  // рядом честно сказало бы «Любой статус» — два противоположных ответа на один вопрос.
  const filters = (
    <>
      <SelectField
        id='people-category'
        label='Разряд'
        value={search.filters[FILTER_CATEGORY] ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_CATEGORY, value === NO_VALUE ? undefined : value))
        }
        options={GUARD_CATEGORIES.map((value) => ({ value, label: `${value} разряд` }))}
        noneLabel='Любой разряд'
        placeholder='Любой разряд'
        // Пояснение появляется, только когда отбор включён: объяснять, откуда берётся разряд,
        // человеку, который разрядом не отбирает, незачем, а постоянное — лишняя строка текста.
        hint={
          search.filters[FILTER_CATEGORY] === undefined
            ? undefined
            : 'Разряд берётся из действующего удостоверения охранника.'
        }
      />

      <SiteFilter
        value={search.filters[FILTER_SITE]}
        onChange={(value) => apply(setFilter(search, FILTER_SITE, value))}
      />

      <RegionFilter
        value={search.filters[FILTER_REGION]}
        onChange={(value) => apply(setFilter(search, FILTER_REGION, value))}
      />

      <div className='flex items-center justify-between gap-2'>
        <label htmlFor='people-debt' className='text-paragraph-sm text-text-sub-600'>
          Только с непогашенным долгом
        </label>
        <Switch.Root
          id='people-debt'
          checked={search.filters[FILTER_DEBT] === 'true'}
          onCheckedChange={(checked) =>
            apply(setFilter(search, FILTER_DEBT, checked ? 'true' : undefined))
          }
        />
      </div>

      <div className='border-t border-stroke-soft-200 pt-3'>
        {/* «реестр» в подписи обязательно: справа в карточке человека стоит вторая
            выгрузка — расчётный лист, — и обе кнопки видны на экране одновременно. Пока обе
            назывались «Выгрузить в Excel», человек выбирал между ними наугад. */}
        <ExportButton
          // В файле тот же срез, что на экране (`exportEmployees`: собирает СЕРВЕР), поэтому
          // состояние открытой вкладки едет сюда вместе с остальным отбором: в запрос списка
          // оно не входит, а выгрузка без него отдала бы всех, хотя человек видит только тех,
          // кто на вахте.
          собрать={() => exportEmployees({ ...params, status: статусВыгрузки })}
          что='реестр'
        />
      </div>
    </>
  );

  // Что сказать, когда под вкладкой никого нет. Причин четыре, и слипаться они не имеют права
  // (правило 7): реестр пуст вовсе, пусто под отбором во всех состояниях, пуста одна вкладка,
  // пуста одна вкладка под поиском. Без этого пустая «На вахте» говорила бы «В реестре пока
  // никого нет» вместо причины и отправляла бы человека заводить тех, кто уже заведён.
  const пустое = describeEmpty({
    tab: вкладка,
    counts: счёт,
    query: search.q,
    otherNarrowing: hasFilterNarrowing(view, search),
  });

  function снятьОтбор() {
    // Поиск, отборы и срез по типу оформления уходят; вкладка остаётся, только если человек
    // выбрал её сам. Неявная «Все» (адрес был сужен) отпускается: на чистом адресе реестр
    // открывается так, как открывается всегда.
    const названа = search.filters[FILTER_STATUS];
    const состояние: Record<string, string> =
      названа === undefined || названа === '' ? {} : { [FILTER_STATUS]: вкладка };
    navigate(peopleHref(DEFAULT_VIEW, { ...search, q: undefined, filters: состояние }, rowId), {
      replace: true,
    });
  }

  // Вкладок нет, пока нет данных (там скелет той же высоты) и пока в выборке никого: строка
  // нулей «На вахте 0 · Межвахта 0 · … · Все 0» ничего не сообщает, а причину пустоты говорит
  // само пустое состояние. Нет их и при неполной странице — см. выше.
  const вкладки =
    query.data === undefined ? (
      query.error === null ? (
        <ВкладкиСостоянийСкелет />
      ) : undefined
    ) : страницаПолная && rows.length > 0 ? (
      <ВкладкиСостояний
        активная={вкладка}
        счёт={счёт}
        // Вкладка — это тот же отбор в адресе: replace, один шаг истории на подбор.
        onChange={(следующая) => apply(setFilter(search, FILTER_STATUS, следующая))}
      />
    ) : undefined;

  // «Новый сотрудник» стоит в шапке списка, а не после последней строки: при 133 людях на
  // вахте до неё было восемь с половиной тысяч точек прокрутки (замер 01.10.2026). Кнопка одна на
  // весь раздел: в правой колонке такой же кнопки больше нет, в пустом реестре на неё ссылается
  // текст («кнопкой «Новый сотрудник» вверху списка»).
  const заведение = (
    <Button.Root
      variant='neutral'
      mode='stroke'
      size='xsmall'
      onClick={() => {
        // Кнопка не сереет, а объясняет: выключенная не доводит нажатие до обработчика, и
        // человек, который жмёт и не получает ничего, читает это как сломанную программу
        // (`ui-views.md`). Пример из прогона ролей 02.09.2026: охраннику, у которого в реестре
        // ровно одна строка — он сам, — предлагалось завести сотрудника. Права на это у него нет
        // и не будет. Причина появляется по нажатию, а не висит на экране заранее: заранее это
        // ещё одна строка, которую человек читает каждый раз, а нажимает он кнопку один раз.
        if (!canEdit) {
          setЗапретЗаведения(
            'Заводит людей кадровая служба. Вашей роли это не разрешено: попросите кадры внести человека.',
          );
          return;
        }
        navigate(openPanelHref(route, PANEL_NEW_EMPLOYEE, NEW_EMPLOYEE_BLANK));
      }}
    >
      <Button.Icon as={RiAddLine} />
      Новый сотрудник
    </Button.Root>
  );

  return (
    <ListPane
      caption={captionCount === undefined ? 'Люди' : listCaption(показаннаяВкладка, captionCount)}
      sliceAt={parseMoment(query.data?.generated_at)}
      viewSwitcher={viewSwitcher}
      note={вкладки}
      search={поиск}
      filters={filters}
      appliedFilters={наложено}
      action={заведение}
      actionNote={
        запретЗаведения === null ? undefined : <Hint.Root hasError>{запретЗаведения}</Hint.Root>
      }
      width='имена'
      footer={
        selected.length === 0 ? undefined : (
          <BulkStatusBar
            selected={selected}
            открытаяВкладка={показаннаяВкладка}
            onClear={() => setSelected([])}
            onDone={() => setSelected([])}
            можноПравить={canEdit}
            onStaff={() => {
              // Выделение уезжает в мастер памятью, а не адресом: двадцать опознавателей в
              // адресной строке — это ссылка, которую нельзя ни прочитать, ни переслать.
              запомнитьВыбор(selected);
              return openStaffHref(route);
            }}
            onInvite={
              canInvite
                ? () => {
                    // Вместе с опознавателями едут фамилии: мастер показывает их списком, а
                    // спрашивать людей заново он стал бы ДРУГИМ запросом — выделенный человек
                    // мог бы в него не попасть и исчезнуть из раздачи молча.
                    запомнитьПриглашаемых(
                      rows
                        .filter((человек) => selected.includes(человек.id))
                        .map((человек) => ({ id: человек.id, фамилия: человек.display_name })),
                    );
                    return openInviteHref(route);
                  }
                : undefined
            }
          />
        )
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='реестр людей'
        isEmpty={() => видимые.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiTeamLine}
            title={пустое.title}
            illustration={пустое.kind === 'registry' ? 'люди' : undefined}
            description={пустое.description}
            // Кнопка есть только там, где пустоту можно снять одним шагом: показать остальные
            // вкладки, искать по всем или убрать отбор. У пустого РЕАЛЬНО реестра её нет
            // намеренно: он объясняется дважды — слева «в реестре пока никого нет» с кнопкой,
            // справа «человек не выбран» с той же ссылкой, — два сообщения об одном, причём
            // правое ещё и неверно по смыслу: выбирать некого. Приглашение завести первого
            // остаётся в правой колонке, где есть и место, и объяснение; узкая колонка списка
            // молчит. А кнопка «Новый сотрудник» при пустоте от отбора или вкладки отвечала бы
            // не на тот вопрос: людей не видно из-за отбора, а не из-за того, что их нет.
            action={
              пустое.action === null ? undefined : (
                <Button.Root
                  variant='neutral'
                  mode='stroke'
                  size='xsmall'
                  onClick={
                    пустое.action.kind === 'clear-narrowing'
                      ? снятьОтбор
                      : () => apply(setFilter(search, FILTER_STATUS, STATUS_ALL))
                  }
                >
                  <Button.Icon
                    as={пустое.action.kind === 'clear-narrowing' ? RiFilterOffLine : RiTeamLine}
                  />
                  {пустое.action.label}
                </Button.Root>
              )
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {видимые.map((employee) => (
              <ListRow
                key={employee.id}
                to={peopleHref(view, search, employee.id)}
                selected={employee.id === rowId}
                title={employee.display_name}
                // Состояние вахты стоит меткой справа и в подписи НЕ повторяется. Пока
                // повторялось, в строке было два «На вахте» подряд — серым и цветным, — а
                // из-за лишнего слова обрезался объект: «ПромРесурс Административный · Но…».
                //
                // Метка в строке ровно ОДНА, и это состояние вахты. Долг стоял второй меткой
                // и вместе со статусом съедал половину колонки: у человека в межвахтовом
                // отдыхе с долгом от объекта оставалось «ТехноПарк Северный ·…», а город
                // пропадал совсем. Метки не сжимаются, поэтому в жертву приносилось ровно
                // то, ради чего в список и смотрят.
                //
                // Долг переехал в подпись ПЕРВЫМ словом: это денежный факт, и он не имеет
                // права обрезаться раньше города. Красным он при этом не становится — в
                // строке уже есть красный, и он занят порогом подсветки в пульте.
                //
                // Города нет — не пишем ничего, как в списке объектов. Аудит 23.09.2026 по
                // копии боевой базы: город не заполнен ни у кого, и «город не указан» стоял в
                // каждой из 315 строк подряд. Приписка, одинаковая у всех, ничего не различает
                // и только прячет то, что различает. В карточке человека пустой город
                // по-прежнему показан пустым полем с пояснением.
                subtitle={[
                  ...(employee.has_debt ? ['есть долг'] : []),
                  employee.site_name ?? 'без объекта',
                  ...(employee.city == null || employee.city.trim() === '' ? [] : [employee.city]),
                ].join(' · ')}
                // Метка состояния нужна там, где в списке смешаны разные состояния («Все»). На
                // вкладке одного состояния она стоит у каждой из 133 строк и ничего не различает,
                // а ширину съедает у объекта («Шлюмберже "РВЦ и Радиус-Сервис" г. Не…»): вкладка и
                // заголовок «133 человека · на вахте» уже называют состояние (замечание живого
                // прохода по боевым данным 01.10.2026; так же убрана метка в списке ведомостей).
                // У уволенного метка несёт то, чего нет больше нигде в строке: когда уволен.
                badge={метка(employee, показаннаяВкладка === STATUS_ALL)}
                // Галочка выбора рисуется и по праву раздавать ссылки, а не только по
                // праву править карточки. Иначе у руководства и операционного руководства,
                // которым доступы как раз доверены, выделять было НЕЧЕМ — и кнопка «Пригласить
                // выбранных» оказывалась недостижимой при выданном праве. Ровно этот класс
                // дефекта («право есть, кнопки нет») породил обе жалобы заказчика.
                select={
                  canEdit || canInvite ? (
                    <RowCheckbox
                      id={`people-pick-${employee.id}`}
                      label={`Выбрать: ${employee.display_name}`}
                      checked={selected.includes(employee.id)}
                      onCheckedChange={(checked) =>
                        setSelected((current) =>
                          checked
                            ? [...current, employee.id]
                            : current.filter((id) => id !== employee.id),
                        )
                      }
                    />
                  ) : undefined
                }
                // Постоянно видна, как только выбрана хоть одна строка: человек уже в режиме
                // выбора, и прятать от него остальные галочки значит заставлять водить мышью
                // по списку, чтобы найти, куда нажимать.
                selectVisible={selected.length > 0}
              />
            ))}

            {data.items.length < data.total && (
              <div className='px-4 py-2'>
                <Hint.Root>
                  Показаны первые {data.items.length} из {data.total}. Сузьте отбор — иначе человек,
                  которого вы ищете, может остаться за пределами страницы.
                </Hint.Root>
              </div>
            )}
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}

/**
 * Отбор по объекту. Список объектов берётся из раздела «Объекты» — того же справочника, что
 * человек видит там: два разных списка объектов в одной системе означали бы, что один из них
 * устарел, и узнаётся это только по жалобе.
 */
/**
 * Отбор по региону объекта.
 *
 * Список регионов собирается из справочника объектов, а не спрашивается у сервера отдельным
 * маршрутом: объекты уже лежат в кэше — их же показывает соседний отбор, — и второй запрос
 * за теми же данными означал бы второй ключ кэша на один список.
 *
 * Отбора по юрлицу рядом нет намеренно, хотя тз называет оба. Юрлицо у заказчика одно
 * (ООО ЧОП «Барк»), и справочник с единственным вариантом — не вопрос, а лишняя строка на
 * экране, которую человек читает каждый раз (`.claude/rules/ui-views.md`). Появится второе —
 * отбор заведём рядом с этим.
 */
function RegionFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const sites = useSitesQuery({ active: true });
  const регионы = [
    ...new Set(
      (sites.data?.items ?? [])
        .map((site) => site.region)
        .filter((регион): регион is string => регион != null && регион !== ''),
    ),
  ].sort((левый, правый) => левый.localeCompare(правый, 'ru'));

  return (
    <SelectField
      id='people-region'
      label='Регион'
      value={value ?? NO_VALUE}
      onChange={(next) => onChange(next === NO_VALUE ? undefined : next)}
      options={регионы.map((регион) => ({ value: регион, label: регион }))}
      noneLabel='Любой регион'
      placeholder='Любой регион'
      pending={sites.isPending}
      failed={sites.isError}
      failedError={sites.error as ApiError | null}
      emptyNote='У объектов не проставлен регион: отбирать по нему не по чему.'
      hint={
        value === undefined
          ? undefined
          : 'Регион берётся у объекта, на который человек назначен сегодня.'
      }
    />
  );
}

function SiteFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const sites = useSitesQuery({ active: true });
  return (
    <SelectField
      id='people-site'
      label='Объект'
      value={value ?? NO_VALUE}
      onChange={(next) => onChange(next === NO_VALUE ? undefined : next)}
      options={(sites.data?.items ?? []).map((site) => ({ value: site.id, label: site.name }))}
      noneLabel='Любой объект'
      placeholder='Любой объект'
      pending={sites.isPending}
      failed={sites.isError}
      failedError={sites.error as ApiError | null}
      emptyNote='Действующих объектов нет: отбирать людей по объекту не по чему.'
      hint={
        value === undefined ? undefined : 'Считаются живые назначения на сегодня, а не вся история.'
      }
    />
  );
}
